import React, { useEffect, useState } from "react";
import { get, post, put, remove } from "../../../redux/ApiHelper";
import { errorToast, successToast } from "../../../utils/toast.util";
import { QuestionPaperInterface } from "../../../interfaces/question-paper.interface";
import { QuestionInterface } from "../../../interfaces/question.interface";
import {
    Box,
    Typography,
    IconButton,
    Chip,
    TextField,
} from "@mui/material";
import { Delete, Publish, Visibility, Edit } from "@mui/icons-material";
import TableComponent from "../../../components/table/TableComponent";
import { Column } from "../../../interfaces/table.interface";
import {
    QuestionPaperFormDialog,
    QuestionPaperViewDialog,
} from "../../../components/examination/QuestionPaperDialogs";
import { RootState } from "../../../redux/store";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { joinSubjectChips, normalizeSubjectList } from "../../../utils/subjectChips.util";

const MandatoryQuestionPapers = () => {
    const navigate = useNavigate();
    const [questionPapers, setQuestionPapers] = useState<QuestionPaperInterface[]>([]);
    const [filteredPapers, setFilteredPapers] = useState<QuestionPaperInterface[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [viewModalOpen, setViewModalOpen] = useState<boolean>(false);
    const [formModalOpen, setFormModalOpen] = useState<boolean>(false);
    const [selectedPaper, setSelectedPaper] = useState<QuestionPaperInterface | null>(null);
    const [editingPaper, setEditingPaper] = useState<QuestionPaperInterface | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [subjects, setSubjects] = useState<string[]>([]);
    const [availableQuestions, setAvailableQuestions] = useState<QuestionInterface[]>([]);
    const { signedAdmin } = useSelector((state: RootState) => state.admin);

    // Form state for editing
    const [formData, setFormData] = useState<Partial<QuestionPaperInterface>>({
        title: "",
        description: "",
        subject: "",
        class: "",
        examType: "mandatory",
        examStartDateTime: "",
        durationInMinutes: 0,
        fullMarks: 0,
        passMarks: 0,
        questions: [],
    });

    // Validation errors
    const [errors, setErrors] = useState<{
        title?: string;
        subject?: string;
        class?: string;
        examStartDateTime?: string;
        durationInMinutes?: string;
        fullMarks?: string;
        passMarks?: string;
        questions?: string;
    }>({});

    const [publishReviewMode, setPublishReviewMode] = useState<boolean>(false);
    const [publishing, setPublishing] = useState<boolean>(false);

    useEffect(() => {
        fetchQuestionPapers();
        fetchSubjects();
    }, []);

    useEffect(() => {
        if (formData.subject) {
            const seeds = (formData.questions || []).filter(
                (q: any) => q && typeof q === "object" && q._id
            );
            fetchQuestionsBySubject(formData.subject, seeds);
        } else {
            setAvailableQuestions([]);
        }
    }, [formData.subject]);

    useEffect(() => {
        const lowerQuery = searchQuery.toLowerCase();
        const filtered = questionPapers.filter(
            (p) =>
                p.title?.toLowerCase().includes(lowerQuery) ||
                p.subject?.toLowerCase().includes(lowerQuery)
        );
        setFilteredPapers(filtered);
    }, [questionPapers, searchQuery]);

    const fetchQuestionPapers = async () => {
        try {
            const response = await get("/admin/examination/question-papers", {
                examType: "mandatory",
            });
            setQuestionPapers(response.data || []);
            setFilteredPapers(response.data || []);
        } catch (error) {
            console.error("Error fetching question papers:", error);
            errorToast("Failed to fetch question papers");
        }
    };

    const fetchSubjects = async () => {
        try {
            const response = await get("/admin/subject/get-all");
            setSubjects(response.subjects || []);
        } catch (error) {
            console.error("Error fetching subjects:", error);
        }
    };

    const fetchQuestionsBySubject = async (subject: string, seedQuestions: any[] = []) => {
        try {
            const response = await get("/admin/examination/questions", { subject });
            const bank = response.data || [];
            const byId = new Map<string, QuestionInterface>();
            for (const q of [...seedQuestions, ...bank]) {
                if (!q || typeof q !== "object" || !(q as any)._id) continue;
                byId.set(String((q as any)._id), q as QuestionInterface);
            }
            setAvailableQuestions(Array.from(byId.values()));
        } catch (error) {
            console.error("Error fetching questions:", error);
            const seeds = seedQuestions.filter((q) => q && typeof q === "object" && q._id);
            setAvailableQuestions(seeds);
        }
    };

    const fetchQuestionPaperDetails = async (paperId: string) => {
        try {
            const response = await get(`/admin/examination/question-paper/${paperId}`);
            return response.data;
        } catch (error) {
            console.error("Error fetching question paper details:", error);
            errorToast("Failed to fetch question paper details");
            return null;
        }
    };


    const handleDelete = async (paperId: string) => {
        if (!window.confirm("Are you sure you want to delete this question paper?")) {
            return;
        }

        try {
            await remove(`/admin/examination/question-paper/${paperId}`);
            successToast("Question paper deleted successfully");
            fetchQuestionPapers();
        } catch (error) {
            console.error("Error deleting question paper:", error);
            errorToast("Failed to delete question paper");
        }
    };

    const handlePublish = async (paperId: string) => {
        const detailedPaper = await fetchQuestionPaperDetails(paperId);
        if (detailedPaper) {
            setSelectedPaper(detailedPaper);
            setPublishReviewMode(true);
            setViewModalOpen(true);
        }
    };

    const confirmPublish = async () => {
        const paperId = selectedPaper?._id;
        if (!paperId) return;

        try {
            setPublishing(true);
            await put(`/admin/examination/question-paper/${paperId}/publish`, {
                adminEmail: signedAdmin.email,
                adminRole: signedAdmin.role,
                adminType: signedAdmin.type,
            });
            successToast("Question paper published successfully");
            handleCloseViewModal();
            fetchQuestionPapers();
        } catch (error) {
            console.error("Error publishing question paper:", error);
            errorToast("Failed to publish question paper");
        } finally {
            setPublishing(false);
        }
    };

    const handleViewDetails = async (paperId: string) => {
        const paper = questionPapers.find((p) => p._id === paperId);
        if (paper) {
            const detailedPaper = await fetchQuestionPaperDetails(paperId);
            if (detailedPaper) {
                setSelectedPaper(detailedPaper);
                setPublishReviewMode(false);
                setViewModalOpen(true);
            }
        }
    };

    const handleEdit = async (paperId: string) => {
        const paper = questionPapers.find((p) => p._id === paperId);
        if (paper) {
            const detailedPaper = await fetchQuestionPaperDetails(paperId);
            if (detailedPaper) {
                setEditingPaper(detailedPaper);
                const paperSubjects = normalizeSubjectList(detailedPaper.subject);
                const periodSubjects = normalizeSubjectList(
                    typeof detailedPaper.tuitionPeriod === "object"
                        ? (detailedPaper.tuitionPeriod as any)?.subjects
                        : undefined
                );
                setFormData({
                    title: detailedPaper.title,
                    description: detailedPaper.description || "",
                    subject: joinSubjectChips(paperSubjects.length ? paperSubjects : periodSubjects),
                    class: detailedPaper.class || "",
                    examType: "mandatory",
                    examStartDateTime: detailedPaper.examStartDateTime
                        ? new Date(detailedPaper.examStartDateTime).toISOString().slice(0, 16)
                        : "",
                    durationInMinutes: detailedPaper.durationInMinutes,
                    fullMarks: detailedPaper.fullMarks,
                    passMarks: detailedPaper.passMarks,
                    questions: Array.isArray(detailedPaper.questions) ? detailedPaper.questions : [],
                });
                const seedQuestions = (detailedPaper.questions || []).filter(
                    (q: any) => q && typeof q === "object" && q._id
                );
                setAvailableQuestions(seedQuestions);
                setFormModalOpen(true);
            }
        }
    };

    const handleCloseViewModal = () => {
        setViewModalOpen(false);
        setSelectedPaper(null);
        setPublishReviewMode(false);
    };

    const handleCloseFormModal = () => {
        setFormModalOpen(false);
        setEditingPaper(null);
        setAvailableQuestions([]);
        setErrors({});
        setFormData({
            title: "",
            description: "",
            subject: "",
            class: "",
            examType: "mandatory",
            examStartDateTime: "",
            durationInMinutes: 0,
            fullMarks: 0,
            passMarks: 0,
            questions: [],
        });
    };

    const validateForm = (): boolean => {
        const newErrors: typeof errors = {};

        if (!formData.title || formData.title.trim() === "") {
            newErrors.title = "Title is required";
        }

        if (!formData.subject || formData.subject.trim() === "") {
            newErrors.subject = "Subject is required";
        }

        if (!formData.class || String(formData.class).trim() === "") {
            newErrors.class = "Class is required";
        }

        if (!formData.examStartDateTime) {
            newErrors.examStartDateTime = "Exam start date and time is required";
        } else {
            const examDate = new Date(formData.examStartDateTime);
            const now = new Date();
            if (examDate < now) {
                newErrors.examStartDateTime = "Exam start date must be in the future";
            }
        }

        if (!formData.durationInMinutes || formData.durationInMinutes <= 0) {
            newErrors.durationInMinutes = "Duration must be greater than 0";
        }

        if (!formData.fullMarks || formData.fullMarks <= 0) {
            newErrors.fullMarks = "Full marks must be greater than 0";
        }

        if (!formData.passMarks || formData.passMarks <= 0) {
            newErrors.passMarks = "Pass marks must be greater than 0";
        } else if (formData.fullMarks && formData.passMarks > formData.fullMarks) {
            newErrors.passMarks = "Pass marks cannot be greater than full marks";
        }

        setErrors(newErrors);
        const messages = Object.values(newErrors).filter(Boolean);
        if (messages.length) {
            errorToast(messages.join(". "));
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            const payload = {
                ...formData,
                questions: (formData.questions || [])
                    .map((q: any) => (typeof q === "string" ? q : q?._id))
                    .filter(Boolean),
                adminEmail: signedAdmin.email,
                adminRole: signedAdmin.role,
                adminType: signedAdmin.type,
            };
            if (editingPaper?._id) {
                await put(`/admin/examination/question-paper/${editingPaper._id}`, payload);
                successToast("Question paper updated successfully");
                handleCloseFormModal();
                fetchQuestionPapers();
            } else {
                await post("/admin/examination/question-paper/create", payload);
                successToast("Question paper created successfully");
                handleCloseFormModal();
                fetchQuestionPapers();
            }
        } catch (error) {
            console.error("Error updating question paper:", error);
            errorToast(editingPaper?._id ? "Failed to update question paper" : "Failed to create question paper");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleQuestion = (questionId: string) => {
        const currentQuestions = formData.questions || [];
        const questionIds = currentQuestions.map((q: any) => (typeof q === "string" ? q : q._id));
        const newQuestions = questionIds.includes(questionId)
            ? questionIds.filter((id) => id !== questionId)
            : [...questionIds, questionId];
        setFormData({ ...formData, questions: newQuestions });
        if (errors.questions && newQuestions.length > 0) {
            setErrors({ ...errors, questions: undefined });
        }
    };

    const columns: Column[] = [
        { select: "title", header: "Title", align: "left" },
        { select: "subject", header: "Subject", align: "left" },
        {
            select: "tuitionPeriod",
            header: "Student",
            align: "left",
            render: (value: any) => {
                if (typeof value === "object" && value?.student) {
                    const student = value.student as any;
                    return (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {student.profilePicture && (
                                <img
                                    src={student.profilePicture}
                                    alt={student.name}
                                    style={{ width: 30, height: 30, borderRadius: "50%" }}
                                />
                            )}
                            <Typography variant="body2">{student.name || "N/A"}</Typography>
                        </Box>
                    );
                }
                return "N/A";
            },
        },
        {
            select: "examStartDateTime",
            header: "Exam Date",
            render: (value: string | Date) =>
                new Date(value).toLocaleString(),
        },
        { select: "durationInMinutes", header: "Duration (min)" },
        { select: "fullMarks", header: "Full Marks" },
        { select: "passMarks", header: "Pass Marks" },
        {
            select: "isPublished",
            header: "Status",
            render: (value: boolean) => (
                <Chip
                    label={value ? "Published" : "Draft"}
                    color={value ? "success" : "default"}
                    size="small"
                />
            ),
        },
        {
            select: "actions",
            header: "Actions",
            render: (_: any, row: QuestionPaperInterface) => (
                <Box>
                    <IconButton
                        size="small"
                        onClick={() => handleViewDetails(row._id!)}
                        color="primary"
                    >
                        <Visibility />
                    </IconButton>
                    {!row.isPublished && (
                        <IconButton
                            size="small"
                            onClick={() => handleEdit(row._id!)}
                            color="primary"
                        >
                            <Edit />
                        </IconButton>
                    )}
                    {!row.isPublished && (
                        <IconButton
                            size="small"
                            onClick={() => handlePublish(row._id!)}
                            color="success"
                        >
                            <Publish />
                        </IconButton>
                    )}
                    <IconButton
                        size="small"
                        onClick={() => handleDelete(row._id!)}
                        color="error"
                    >
                        <Delete />
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
                <Typography variant="h4" className="text-white">Mandatory Exam Papers</Typography>
            </Box>

            <TextField
                fullWidth
                label="Search Question Papers"
                variant="outlined"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                        color: "#ffffff",
                        "& fieldset": {
                            borderColor: "#ffffff",
                        },
                        "&:hover fieldset": {
                            borderColor: "#ffffff",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "#ffffff",
                        },
                    },
                    "& .MuiInputLabel-root": {
                        color: "#ffffff",
                        "&.Mui-focused": {
                            color: "#ffffff",
                        },
                    },
                    "& .MuiInputBase-input": {
                        color: "#ffffff",
                    },
                }}
            />

            <TableComponent
                data={filteredPapers}
                columns={columns}
            />

            <QuestionPaperViewDialog
                open={viewModalOpen}
                onClose={handleCloseViewModal}
                selectedPaper={selectedPaper}
                primaryActionText={publishReviewMode ? (publishing ? "Publishing..." : "Publish") : undefined}
                primaryActionDisabled={
                    !publishReviewMode ||
                    publishing ||
                    !selectedPaper?._id ||
                    (((selectedPaper as any)?.questions?.length ?? 0) === 0)
                }
                onPrimaryAction={publishReviewMode ? confirmPublish : undefined}
            />

            <QuestionPaperFormDialog
                open={formModalOpen}
                mode={editingPaper?._id ? "edit" : "create"}
                onClose={handleCloseFormModal}
                onSubmit={handleSubmit}
                loading={loading}
                questionPaperId={editingPaper?._id}
                signedAdmin={signedAdmin}
                onPaperUpdated={(paper) => {
                    if (paper?._id) setEditingPaper(paper);
                    fetchQuestionPapers();
                }}
                subjects={subjects}
                availableQuestions={availableQuestions}
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                setErrors={setErrors}
                onToggleQuestion={handleToggleQuestion}
                onGoToQuestionsPage={() => navigate("/examination/questions")}
                titleText={editingPaper?._id ? "Edit Question Paper" : "Create Question Paper"}
                submitText={editingPaper?._id ? "Update" : "Create"}
                createExtras={{
                    tuitionPeriodId:
                        typeof editingPaper?.tuitionPeriod === "object"
                            ? (editingPaper.tuitionPeriod as any)?._id
                            : editingPaper?.tuitionPeriod,
                }}
            />
        </Box>
    );
};

export default MandatoryQuestionPapers;
